import type { BadgeProps } from '@/components/ui/badge'

// ============================================
// Types — Allocation Lifecycle
// ============================================

export type AllocationStatus = 'reserved' | 'active' | 'completed' | 'cancelled'
export type PresenceStatus = 'expected' | 'present' | 'away' | 'departed'
export type PositionType = 'hangar' | 'tiedown' | 'ramp' | 'unknown'

/** Derived render mode for the 3D map (from status + presence) */
export type AllocationRenderMode =
  | 'solid'            // active + present — normal rendering
  | 'ghost_away'       // active + away — translucent, shows expected return
  | 'ghost_incoming'   // reserved + expected — dashed outline / coming soon
  | 'ghost_returning'  // active + expected (edge case: departed then re-expected)

// ============================================
// Enum Arrays for Iteration
// ============================================

export const ALLOCATION_STATUSES = ['reserved', 'active', 'completed', 'cancelled'] as const
export const PRESENCE_STATUSES = ['expected', 'present', 'away', 'departed'] as const
export const POSITION_TYPES = ['hangar', 'tiedown', 'ramp', 'unknown'] as const

// ============================================
// Enum Labels
// ============================================

export const ALLOCATION_STATUS_LABELS: Record<AllocationStatus, string> = {
  reserved: 'Reserved',
  active: 'Active',
  completed: 'Completed',
  cancelled: 'Cancelled',
}

export const PRESENCE_STATUS_LABELS: Record<PresenceStatus, string> = {
  expected: 'Expected',
  present: 'Present',
  away: 'Away',
  departed: 'Departed',
}

export const POSITION_TYPE_LABELS: Record<PositionType, string> = {
  hangar: 'Hangar',
  tiedown: 'Tiedown',
  ramp: 'Ramp',
  unknown: 'Unknown',
}

// ============================================
// Badge Variants for Status
// ============================================

type BadgeVariant = BadgeProps['variant']

export const ALLOCATION_STATUS_VARIANTS: Record<AllocationStatus, BadgeVariant> = {
  reserved: 'secondary',
  active: 'default',
  completed: 'outline',
  cancelled: 'destructive',
}

export const PRESENCE_STATUS_VARIANTS: Record<PresenceStatus, BadgeVariant> = {
  expected: 'secondary',
  present: 'default',
  away: 'outline',
  departed: 'outline',
}

export const POSITION_TYPE_VARIANTS: Record<PositionType, BadgeVariant> = {
  hangar: 'default',
  tiedown: 'secondary',
  ramp: 'secondary',
  unknown: 'outline',
}

// ============================================
// Table Row Type (with joined data)
// ============================================

export interface AllocationTableRow {
  // Core
  id: string
  organization_id: string
  location_id: string
  aircraft_id: string

  // Aircraft (with joined data)
  tail_number: string | null
  aircraft_type_name: string | null

  // Location (with joined data)
  location_name: string | null

  // Space
  area_id: string | null
  area_name: string | null
  unit_id: string | null
  unit_code: string | null
  zone_id: string | null

  // Position
  position_type: PositionType
  position_x: number
  position_y: number
  position_z: number
  rotation: number
  custom_label: string | null
  show_label: boolean | null
  color_override: string | null

  // Lifecycle
  status: AllocationStatus
  presence: PresenceStatus
  starts_at: string | null
  ends_at: string | null
  expected_return_at: string | null

  // Collision
  has_collision: boolean | null
  collision_details: unknown | null

  // Links
  lease_contract_id: string | null
  transient_stay_id: string | null

  // Audit
  created_at: string
  created_by: string | null
  updated_at: string | null
  updated_by: string | null
  deleted_at: string | null
  deleted_by: string | null
}

// ============================================
// Form Data Type
// ============================================

export interface AllocationFormData {
  // Required fields
  aircraft_id: string
  location_id: string
  position_type: PositionType

  // Position (with sensible defaults)
  position_x?: number
  position_y?: number
  position_z?: number
  rotation?: number

  // Optional space allocation
  area_id?: string | null
  unit_id?: string | null
  zone_id?: string | null

  // Display customization
  custom_label?: string | null
  show_label?: boolean
  color_override?: string | null

  // Lifecycle
  status?: AllocationStatus
  presence?: PresenceStatus
  starts_at?: string
  ends_at?: string | null

  // Links
  lease_contract_id?: string | null
  transient_stay_id?: string | null
}

// ============================================
// Filter Type
// ============================================

export interface AllocationFilters {
  search?: string
  status?: AllocationStatus[]
  positionType?: PositionType[]
  areaId?: string
  locationId?: string
  withCollisions?: boolean
}

// ============================================
// Helper Functions
// ============================================

export function formatPosition(x: number, y: number, z: number): string {
  return `(${x.toFixed(1)}, ${y.toFixed(1)}, ${z.toFixed(1)})`
}

export function formatRotation(rotation: number): string {
  return `${rotation.toFixed(1)}°`
}

export function getAllocationDisplayName(
  allocation: Pick<AllocationTableRow, 'tail_number' | 'custom_label'>
): string {
  return allocation.custom_label || allocation.tail_number || 'Unknown'
}

export function isActive(status: AllocationStatus | null): boolean {
  return status === 'active'
}

export function isReserved(status: AllocationStatus | null): boolean {
  return status === 'reserved'
}

export function isCompleted(status: AllocationStatus | null): boolean {
  return status === 'completed'
}

export function isPresent(presence: PresenceStatus | null): boolean {
  return presence === 'present'
}

export function isAway(presence: PresenceStatus | null): boolean {
  return presence === 'away'
}

/**
 * Derive render mode for the 3D map from status + presence.
 * Used by MapboxScene to choose solid vs ghost rendering.
 */
export function deriveRenderMode(
  status: AllocationStatus | null,
  presence: PresenceStatus | null,
): AllocationRenderMode {
  if (status === 'active' && presence === 'present') return 'solid'
  if (status === 'active' && presence === 'away') return 'ghost_away'
  if (status === 'reserved' && presence === 'expected') return 'ghost_incoming'
  if (status === 'active' && presence === 'expected') return 'ghost_returning'
  return 'solid'
}
